import { AssetsProvider, styled } from '@obvio/app'
import { PartnersCarousel } from '@obvio/template'
import { descSort } from '@obvio/utils'
import { AnimatePresence } from 'framer-motion'
import { useState } from 'react'

import { EventArtGrid } from './EventArtGrid'
import { EventArtModal } from './EventArtModal'
import { EventDescription } from './EventDescription'
import { EventFooter } from './EventFooter'
import { EventHeading } from './EventHeading'
import { EventRegister } from './EventRegister'
import { EventRespondSection } from './EventRespondSection'
import { EventResponse } from './EventResponse'
import { EventReveal } from './EventReveal'
import { CenteredSection } from '../CenteredSection'

import type { Schema, ImageAsset } from '@obvio/app'
import type { AllowUndefinedForOptional, DeepPick } from '@obvio/utils'
import type { ReactElement } from 'react'

const SectionOffset = styled(CenteredSection)`
  margin-top: 15vh;
  margin-bottom: ${(theme) => theme.spacing.extraLarge};
  text-transform: uppercase;
`

export type FormStatus = 'form' | 'accept' | 'decline'

export const EVENT_SELECT = {
  id: true,
  title: true,
  description: true,
  date: true,
  endDate: true,
  images: true,
  startTime: true,
  endTime: true,
  invites: true,
  place: true,
  placeUrl: true,
  placeAddress: true,
  rodoRequired: true,
  isVip: true,
  hasPlaceLeft: true,
  eventPartners: {
    image: true,
    url: true,
    title: true,
  },
  artworks: {
    id: true,
    title: true,
    createdAt: true,
    images: true,
    description: true,
    year: true,
    slug: true,
    merchant: {
      id: true,
      name: true,
    },
  },
} as const

export type Event = DeepPick<Schema['Event'], typeof EVENT_SELECT>
export type EventPageProps = AllowUndefinedForOptional<Event>

export function EventPageVip({
  artworks,
  date,
  endDate,
  startTime,
  endTime,
  id,
  images,
  invites,
  title,
  description,
  place,
  placeUrl,
  rodoRequired,
  hasPlaceLeft,
  eventPartners,
}: Omit<EventPageProps, 'isVip'>): ReactElement {
  const [status, setStatus] = useState<FormStatus | null>(null)
  const [selectedArt, setSelectedArt] = useState<string | null>(null)

  const resetStatus = () => setStatus(null)
  const changeStatus = (newStatus: FormStatus) => () => setStatus(newStatus)
  const resetArt = () => setSelectedArt(null)

  const image = images[0] as ImageAsset

  const sortedArtworks = artworks.sort((a, b) =>
    descSort(a.createdAt, b.createdAt),
  )

  const isActive = endDate
    ? Date.now() < new Date(endDate).getTime()
    : Date.now() < new Date(date).getTime()

  return (
    <>
      <SectionOffset>
        <EventHeading
          title={title}
          date={date.toDateString()}
          startTime={startTime ?? undefined}
          endTime={endTime ?? undefined}
          place={place ?? undefined}
          placeUrl={placeUrl ?? undefined}
        />
      </SectionOffset>
      <EventReveal image={image} id={id} />
      <EventDescription description={description} />
      <EventArtGrid artworks={sortedArtworks} selectArt={setSelectedArt} />
      {invites && isActive ? (
        <>
          <EventRespondSection
            setRegister={changeStatus('form')}
            setDecline={changeStatus('decline')}
          />
          <AnimatePresence>
            {status === 'form' && (
              <EventRegister
                id={id}
                close={resetStatus}
                title={title}
                place={place}
                placeUrl={placeUrl}
                date={date.toDateString()}
                accept={changeStatus('accept')}
                rodoRequired={rodoRequired}
                hasPlaceLeft={hasPlaceLeft}
                onNewClick={() => setStatus('form')}
              />
            )}
            {(status === 'decline' || status === 'accept') && (
              <EventResponse
                close={resetStatus}
                decline={status === 'decline'}
                eventId={id}
                onNewClick={() => setStatus('form')}
              />
            )}
          </AnimatePresence>
        </>
      ) : null}
      <CenteredSection>
        <AssetsProvider path={`/event/${id}`}>
          <PartnersCarousel
            partners={eventPartners.map((v) => ({ ...v, image: v.image[0] }))}
          />
        </AssetsProvider>
      </CenteredSection>
      <EventFooter />
      <EventArtModal
        open={Boolean(selectedArt)}
        close={resetArt}
        selected={artworks.find((art) => selectedArt === art.id)}
      />
    </>
  )
}
